import React from 'react'
import {init, getErrorBoundary, ErrorLoggingConfig, OnError} from '@/logger'

// import {ErrorFallbackUI} from '../components/ErrorFallbackUI/ErrorFallbackUI'
import {BUGSNAG_API_KEY, APP_VERSION, BUGSNAG_STAGE} from '../constants'
import {NotAuthorizedError} from '../customErrors'

// Substring identifying the vendored FileStack picker bundle in a stack frame's
// file URL (e.g. static.filestackapi.com/picker/1.39.10/picker.js).
const FILESTACK_PICKER_BUNDLE = 'filestackapi.com/picker'

// Third-party FileStack picker bug: cancelling an in-progress upload throws
// `TypeError: Cannot read properties of undefined (reading 'name')` inside
// picker.js (Jr.cancelUpload), with zero in-project frames, so it escapes to
// window.onerror as an unhandled error. Unlike the FileStack open()/teardown
// rejections hardened in PhotoSelector.tsx (AB#21200, AB#21629), which surface
// through promises our code awaits, this one fires inside FileStack's own event
// dispatch loop — there is no in-project call site or promise to wrap. It is
// benign (the user is cancelling the upload) and unpatchable in-place, so drop
// it here. Scoped to the throw-site (top) frame — NOT any frame in the trace —
// so a genuine app-code TypeError reading 'name' thrown from inside a picker
// callback (MediaAttachment wires onUploadStarted/Finished/Failed) still
// reports: its top frame is app code even though a picker.js caller frame sits
// below it. See BugSnag error 6a15ef528fd7d1b48ccd6ed4.
const isFileStackCancelError = (error: {
  errorClass: string
  errorMessage: string
  stacktrace: {file?: string}[]
}): boolean =>
  error.errorClass === 'TypeError' &&
  error.errorMessage.includes("reading 'name'") &&
  Boolean(error.stacktrace[0]?.file?.includes(FILESTACK_PICKER_BUNDLE))

const filterErrorLogging: OnError = (event) => {
  // Global rules
  // NOTE: I'm keeping this uncommented as I'm doing frequent testing in dev server mode
  // At some point this should probably be added back to prevent sending errors to bugsnag under the 'development' release stage
  // if (!import.meta.env.PROD) {
  //   return false
  // }

  // Per-error Rules
  if (event.errors.length) {
    const error = event.errors[0]
    const ignoredErrors = [NotAuthorizedError]
    if (ignoredErrors.some((err) => err.name === error.errorClass)) {
      return false
    }

    if (isFileStackCancelError(error)) {
      return false
    }
  }

  return true
}

export {filterErrorLogging}

const loggingConfig: ErrorLoggingConfig = {
  apiKey: BUGSNAG_API_KEY,
  appVersion: APP_VERSION,
  appType: 'client',
  releaseStage: BUGSNAG_STAGE,
  onError: [filterErrorLogging],
}

init(loggingConfig)

const ErrorBoundary = getErrorBoundary()

export const Logger = ({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement =>
  ErrorBoundary ? (
    <ErrorBoundary>{children}</ErrorBoundary>
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{children}</> // needed due to conflict of children as React.ReactNode and FC return of React.ReactElement
  )
